import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Image as ImageIcon, Tag, MapPin, FileText } from "lucide-react";
import { getMyDisplayProofById, resolveAssetUrl } from "../services/api";
import "../styles/SaleDetails.css";
import "../styles/DisplayProofDetails.css";

const toTitleCase = (value) =>
  String(value || "")
    .split(/[_\s]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getProofTypeLabel = (proofType, t) => {
  switch (String(proofType || "").toLowerCase()) {
    case "common_display":
      return t("se.displayProof.types.commonDisplay");
    case "display_corner":
      return t("se.displayProof.types.displayCorner");
    case "promo_area":
      return t("se.displayProof.types.promoArea");
    case "others":
      return t("se.displayProof.types.others");
    case "station":
      return t("se.displayProof.types.station");
    case "shelf":
      return t("se.displayProof.types.shelf");
    default:
      return toTitleCase(proofType);
  }
};

function DisplayProofDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { proofData, previousView } = location.state || {};

  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoadingState, setImageLoadingState] = useState({});

  useEffect(() => {
    const proofId = Number(proofData?.proof_id);
    if (!Number.isInteger(proofId) || proofId <= 0) {
      setError(t("se.displayProof.details.noProofData", { defaultValue: "No display proof data found." }));
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        setLoading(true);
        const response = await getMyDisplayProofById(proofId);
        if (response?.success && response?.data) {
          setProof(response.data);
        } else {
          setError(t("se.displayProof.details.errorFetch", { defaultValue: "Failed to fetch display proof details." }));
        }
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            t("se.displayProof.details.errorFetch", {
              defaultValue: "Failed to fetch display proof details.",
            }),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [proofData, t]);

  useEffect(() => {
    if (!Array.isArray(proof?.images)) {
      setImageLoadingState({});
      return;
    }

    const nextState = {};
    proof.images.forEach((image) => {
      nextState[image.image_id] = true;
    });
    setImageLoadingState(nextState);
  }, [proof]);

  const goBack = () =>
    navigate("/promoter", {
      state: { view: previousView || "HISTORY" },
    });

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-MY", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="sale-details-container">
        <div className="sale-items-card">
          <div className="items-header">
            <ImageIcon size={20} />
            <h3>{t("se.displayProof.photosLabel")}</h3>
          </div>
          <div className="proof-images-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={`proof-skeleton-${index}`}
                className="proof-image-skeleton"
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !proof) {
    return (
      <div className="sale-details-container">
        <div className="error-state">
          <p>{error || t("se.displayProof.details.noProofData", { defaultValue: "No display proof data found." })}</p>
          <button onClick={goBack} className="back-button">
            <ArrowLeft size={16} />
            {t("se.saleDetails.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sale-details-container">
      <div className="se-header">
        <div className="se-header-title">
          <button onClick={goBack} className="se-back-btn">
            <ArrowLeft size={24} />
          </button>
          <span className="display-proof-title">
            {t("se.displayProof.details.title", {
              id: proof.proof_id,
              defaultValue: "Display Proof #{{id}}",
            })}
          </span>
        </div>
      </div>

      <div className="sale-details-content">
        <div className="sale-info-card">
          <div className="sale-info-header">
            <div className="sale-date">
              <Calendar size={16} />
              <span>{formatDate(proof.submitted_at)}</span>
            </div>
            <div className="display-proof-type-chip">
              <Tag size={14} />
              <span>{getProofTypeLabel(proof.proof_type, t)}</span>
            </div>
          </div>

          <div className="sale-info-body">
            <div className="sale-info-panel sale-info-panel-highlight">
              <div className="sale-info-panel-icon">
                <MapPin size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.displayProof.outletLabel")}
                </div>
                <div className="sale-info-panel-value">
                  {proof.outlet_name || t("se.displayProof.unknownOutlet")}
                </div>
                <div className="sale-info-panel-subvalue">
                  {proof.outlet_address || t("se.saleDetails.notProvided")}
                </div>
              </div>
            </div>

            <div className="sale-info-panel">
              <div className="sale-info-panel-icon">
                <FileText size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.displayProof.notesLabel")}
                </div>
                <div className="sale-info-panel-value sale-info-panel-value-muted">
                  {proof.notes || t("se.saleDetails.noNotes")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sale-items-card">
          <div className="items-header">
            <ImageIcon size={20} />
            <h3>{t("se.displayProof.photosLabel")}</h3>
          </div>
          {Array.isArray(proof.images) && proof.images.length > 0 ? (
            <div className="proof-images-grid">
              {proof.images.map((image) => (
                <a
                  key={image.image_id}
                  href={resolveAssetUrl(image.image_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="proof-image-link"
                >
                  {imageLoadingState[image.image_id] && (
                    <span className="proof-image-skeleton" aria-hidden="true" />
                  )}
                  <img
                    src={resolveAssetUrl(image.image_url)}
                    alt={t("se.displayProof.details.imageAlt", {
                      id: image.image_id,
                      defaultValue: "Display proof image {{id}}",
                    })}
                    className={`proof-image${
                      imageLoadingState[image.image_id] ? " is-loading" : " is-ready"
                    }`}
                    onLoad={() =>
                      setImageLoadingState((previous) => ({
                        ...previous,
                        [image.image_id]: false,
                      }))
                    }
                    onError={() =>
                      setImageLoadingState((previous) => ({
                        ...previous,
                        [image.image_id]: false,
                      }))
                    }
                  />
                </a>
              ))}
            </div>
          ) : (
            <p className="no-items">
              {t("se.displayProof.details.noImages", { defaultValue: "No images found." })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DisplayProofDetails;
